import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import L from 'leaflet'
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Anchor,
  ArrowLeft,
  Clock3,
  MapPin,
  Navigation,
  RefreshCw,
  Search,
  Ship,
} from 'lucide-react'
import {
  DeliveredFleetVessel,
  fetchDeliveredFleet,
  FleetPositionStatus,
} from '@/lib/deliveredFleetApi'
import { Button } from '@/components/ui/Button'
import { vesselIconInfo, vesselIconSvg } from '@/lib/vesselMapIcons'

const POLL_INTERVAL_MS = 30_000

const statusConfig: Record<FleetPositionStatus, {
  label: string
  dot: string
  marker: string
}> = {
  recent: {
    label: 'Recently seen',
    dot: 'bg-emerald-500',
    marker: '#10b981',
  },
  stale: {
    label: 'Position stale',
    dot: 'bg-amber-500',
    marker: '#f59e0b',
  },
  never_seen: {
    label: 'Awaiting first position',
    dot: 'bg-slate-400',
    marker: '#94a3b8',
  },
}

function vesselLabel(vessel: DeliveredFleetVessel): string {
  return vessel.vessel_name?.trim()
    || vessel.project_name?.trim()
    || vessel.hull_number?.trim()
    || vessel.mmsi
    || 'Unnamed vessel'
}

function vesselIdentity(vessel: DeliveredFleetVessel): string {
  return [
    vessel.hull_number ? `Hull ${vessel.hull_number}` : null,
    vessel.imo_number ? `IMO ${vessel.imo_number}` : null,
    vessel.mmsi ? `MMSI ${vessel.mmsi}` : 'MMSI not verified',
  ].filter(Boolean).join(' · ')
}

function positionLabel(vessel: DeliveredFleetVessel): string {
  if (!vessel.identity_verified) return 'Identity verification required'
  if (!vessel.mmsi) return 'MMSI not verified'
  if (!vessel.tracking_enabled) return 'Tracking disabled'
  return statusConfig[vessel.position_status].label
}

function lifecycleLabel(vessel: DeliveredFleetVessel): string {
  if (vessel.lifecycle_status === 'trials') return 'Active trials'
  if (vessel.lifecycle_status === 'delivered') return 'Delivered'
  return 'Active'
}

function formatDate(value: string | null, includeTime = false): string {
  if (!value) return 'Not yet received'
  const date = new Date(value)
  if (Number.isNaN(date.valueOf())) return value
  return new Intl.DateTimeFormat(undefined, includeTime
    ? { dateStyle: 'medium', timeStyle: 'short' }
    : { dateStyle: 'medium' }
  ).format(date)
}

function positionOf(vessel: DeliveredFleetVessel): [number, number] | null {
  return vessel.latitude !== null && vessel.longitude !== null
    ? [vessel.latitude, vessel.longitude]
    : null
}

const markerIcons = new Map<string, L.DivIcon>()

function markerIcon(vessel: DeliveredFleetVessel, selected: boolean): L.DivIcon {
  const { kind } = vesselIconInfo(vessel.vessel_type)
  const key = `${kind}:${vessel.position_status}:${selected}`
  const cached = markerIcons.get(key)
  if (cached) return cached
  const color = statusConfig[vessel.position_status].marker
  const size = selected ? 42 : 34
  const icon = L.divIcon({
    className: '',
    html: `<span style="display:flex;align-items:center;justify-content:center;box-sizing:border-box;width:${size}px;height:${size}px;border-radius:9999px;background:${color};color:white;border:2px solid white;box-shadow:0 2px 8px rgba(15,23,42,.45)">${vesselIconSvg(vessel.vessel_type)}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  })
  markerIcons.set(key, icon)
  return icon
}

function MapViewport({
  vessels,
  selected,
}: {
  vessels: DeliveredFleetVessel[]
  selected: DeliveredFleetVessel | null
}) {
  const map = useMap()
  const fitted = useRef(false)

  useEffect(() => {
    // ResizeObserver can run before layout settles. Update on the next paint,
    // retaining Leaflet's default pan correction so resized tiles stay aligned.
    let frame = 0
    const resize = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        map.invalidateSize({ animate: false, debounceMoveend: true })
      })
    }
    const observer = new ResizeObserver(resize)
    observer.observe(map.getContainer())
    window.addEventListener('resize', resize)
    document.addEventListener('visibilitychange', resize)
    resize()
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', resize)
      document.removeEventListener('visibilitychange', resize)
    }
  }, [map])

  useEffect(() => {
    const position = selected ? positionOf(selected) : null
    if (position) map.flyTo(position, Math.max(map.getZoom(), 7), { duration: 0.8 })
  }, [map, selected])

  useEffect(() => {
    if (fitted.current) return
    const positions = vessels
      .map(positionOf)
      .filter((position): position is [number, number] => position !== null)
    if (positions.length === 0) return
    fitted.current = true
    if (positions.length === 1) map.setView(positions[0], 7)
    else map.fitBounds(L.latLngBounds(positions), { padding: [42, 42], maxZoom: 7 })
  }, [map, vessels])

  return null
}

export function DeliveredFleetPage() {
  const [vessels, setVessels] = useState<DeliveredFleetVessel[]>([])
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null)

  const loadFleet = useCallback(async (background = false) => {
    if (background) setRefreshing(true)
    else setLoading(true)
    try {
      const response = await fetchDeliveredFleet()
      setVessels(response.vessels)
      setLastRefreshed(response.generated_at)
      setError(null)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load delivered fleet')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    void loadFleet()
    const timer = window.setInterval(() => { void loadFleet(true) }, POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [loadFleet])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return vessels
    return vessels.filter(vessel => [
      vessel.vessel_name,
      vessel.project_name,
      vessel.hull_number,
      vessel.imo_number,
      vessel.mmsi,
      vessel.shipyard_name,
      vessel.ship_owner,
      vessel.flag,
      vessel.call_sign,
      vessel.source_status,
    ].some(value => value?.toLowerCase().includes(query)))
  }, [search, vessels])

  const selected = vessels.find(vessel => vessel.fleet_vessel_id === selectedId) ?? null
  const positionedCount = vessels.filter(vessel => positionOf(vessel) !== null).length
  const recentCount = vessels.filter(vessel => vessel.position_status === 'recent').length

  return (
    <div className="min-h-dvh bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-white lg:flex lg:h-dvh lg:min-h-0 lg:flex-col lg:overflow-hidden">
      <header className="shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <Link
            to="/dashboard"
            className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-300">
            <Ship className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold">Delivered Fleet</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              The latest known positions of vessels your team has delivered
            </p>
          </div>
          <div className="text-right text-xs text-slate-500 dark:text-slate-400">
            <div>{vessels.length} vessels · {recentCount} recently seen</div>
            <div>{lastRefreshed ? `Updated ${formatDate(lastRefreshed, true)}` : 'Waiting for data'}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void loadFleet(true) }}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {error && (
        <div className="mx-4 sm:mx-6 mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
          {error}. The last successfully loaded positions remain on screen.
        </div>
      )}

      <main className="p-4 sm:p-6 lg:min-h-0 lg:flex-1">
        <div className="grid grid-cols-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:h-full lg:min-h-0 lg:grid-cols-[22rem_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]">
          <aside className="flex h-80 min-h-0 flex-col overflow-hidden border-b border-slate-200 dark:border-slate-800 lg:h-full lg:border-b-0 lg:border-r">
            <div className="shrink-0 p-4 border-b border-slate-200 dark:border-slate-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Search vessel, hull, IMO or MMSI"
                  className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {positionedCount} of {vessels.length} vessels have reported a position
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                All delivered/active fleet records are listed, even without AIS. Only received positions appear on the map. Checking every 30 seconds does not guarantee a new AIS report.
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto" aria-label="Fleet vessel list">
              {loading ? (
                <div className="p-8 text-center text-sm text-slate-500">Loading delivered fleet…</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center">
                  <Anchor className="mx-auto mb-3 h-8 w-8 text-slate-300 dark:text-slate-600" />
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {vessels.length === 0 ? 'No delivered vessels yet' : 'No matching vessels'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {vessels.length === 0
                      ? 'No active or delivered vessels are visible in the fleet register.'
                      : 'Try another vessel name, hull, IMO or MMSI.'}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filtered.map(vessel => {
                    const status = statusConfig[vessel.position_status]
                    const active = vessel.fleet_vessel_id === selectedId
                    const typeIcon = vesselIconInfo(vessel.vessel_type)
                    return (
                      <li key={vessel.fleet_vessel_id}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(vessel.fleet_vessel_id)}
                          className={`w-full px-4 py-3 text-left transition ${
                            active
                              ? 'bg-blue-50 dark:bg-blue-950/35'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <span className="relative mt-0.5 shrink-0 text-slate-500" title={typeIcon.label}>
                              <svg role="img" aria-label={typeIcon.label} viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                {typeIcon.paths.map(d => <path key={d} d={d} />)}
                              </svg>
                              <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border border-white ${status.dot}`} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold">{vesselLabel(vessel)}</div>
                              <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                                {vesselIdentity(vessel)}
                              </div>
                              <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                                <Clock3 className="h-3 w-3" />
                                <span>{vessel.last_seen_at ? formatDate(vessel.last_seen_at, true) : positionLabel(vessel)}</span>
                              </div>
                            </div>
                            {positionOf(vessel) && <MapPin className="h-4 w-4 shrink-0 text-slate-400" />}
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </aside>

          <section aria-label="Fleet map" className="relative h-[60dvh] min-h-0 min-w-0 lg:h-full">
            <MapContainer
              center={[20, 0]}
              zoom={2}
              minZoom={2}
              worldCopyJump
              className="h-full w-full bg-slate-200"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapViewport vessels={vessels} selected={selected} />
              {vessels.map(vessel => {
                const position = positionOf(vessel)
                if (!position) return null
                return (
                  <Marker
                    key={vessel.fleet_vessel_id}
                    position={position}
                    icon={markerIcon(vessel, vessel.fleet_vessel_id === selectedId)}
                    title={`${vesselLabel(vessel)} — ${vesselIconInfo(vessel.vessel_type).label}`}
                    alt={`${vesselLabel(vessel)} — ${vesselIconInfo(vessel.vessel_type).label}`}
                    eventHandlers={{ click: () => setSelectedId(vessel.fleet_vessel_id) }}
                  >
                    <Popup>
                      <div className="min-w-[13rem] text-slate-900">
                        <div className="font-semibold">{vesselLabel(vessel)}</div>
                        <div className="mt-1 text-xs text-slate-600">
                          {vesselIdentity(vessel)}
                        </div>
                        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                          <dt className="text-slate-500">Last seen</dt>
                          <dd>{formatDate(vessel.last_seen_at, true)}</dd>
                          <dt className="text-slate-500">Speed</dt>
                          <dd>{vessel.speed_over_ground !== null ? `${vessel.speed_over_ground.toFixed(1)} kn` : '—'}</dd>
                          <dt className="text-slate-500">Course</dt>
                          <dd>{vessel.course_over_ground !== null ? `${Math.round(vessel.course_over_ground)}°` : '—'}</dd>
                          <dt className="text-slate-500">Fleet status</dt>
                          <dd>{lifecycleLabel(vessel)}</dd>
                          {vessel.delivered_at && (
                            <>
                              <dt className="text-slate-500">Delivered</dt>
                              <dd>{formatDate(vessel.delivered_at)}</dd>
                            </>
                          )}
                        </dl>
                        {vessel.project_id && (
                          <Link
                            to={`/projects/${vessel.project_id}`}
                            className="mt-3 inline-flex text-xs font-medium text-blue-600 hover:underline"
                          >
                            Open project
                          </Link>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                )
              })}
            </MapContainer>

            <div className="pointer-events-none absolute bottom-5 left-1/2 z-[500] flex -translate-x-1/2 items-center gap-3 rounded-full bg-white/95 px-4 py-2 text-[11px] text-slate-600 shadow-lg backdrop-blur dark:bg-slate-900/95 dark:text-slate-300">
              {(Object.keys(statusConfig) as FleetPositionStatus[]).map(status => (
                <span key={status} className="flex items-center gap-1.5 whitespace-nowrap">
                  <span className={`h-2 w-2 rounded-full ${statusConfig[status].dot}`} />
                  {statusConfig[status].label}
                </span>
              ))}
            </div>

            {selected && !positionOf(selected) && (
              <div className="absolute left-1/2 top-5 z-[500] w-[min(90%,28rem)] -translate-x-1/2 rounded-lg border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
                <div className="flex items-start gap-3">
                  <Navigation className="mt-0.5 h-5 w-5 text-slate-400" />
                  <div>
                    <div className="text-sm font-semibold">{vesselLabel(selected)}</div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {vesselIdentity(selected)}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {selected.identity_verified && selected.mmsi && selected.tracking_enabled
                        ? `Waiting for an AIS position from MMSI ${selected.mmsi}. No last-known position is available from the current live feed.`
                        : `${positionLabel(selected)}. Vessel details remain available without a map position.`}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {selected.shipyard_name} {selected.vessel_type ? `· ${selected.vessel_type}` : ''}
                    </p>
                    {selected.imo_number && /^\d{7}$/.test(selected.imo_number) && (
                      <a
                        href={`https://www.vesselfinder.com/vessels/details/${selected.imo_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-block text-xs font-medium text-blue-600 hover:underline"
                      >
                        View external vessel details
                      </a>
                    )}
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      External AIS coverage and position age may differ.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}
